import { lightTheme } from '@/customTheme'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@/store/store'
import { selectUser } from '@/store/slices/user/selectors'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { SyncManager } from '@/firebase/SyncManager'
import ButtonCustom from '@/components/ButtonCustom'
import DefaultButton from '@/components/DefaultButton'
import { SizeButton } from '@/constants/sizeButton'
import { KIND } from 'baseui/button'
import { customAmplitude } from '@/utils/customAmplitude'
import { useTranslation } from 'react-i18next'
import { NavbarButtonBaseProps } from '../navbarConfig'
import SuperIcon from '../../Icons/SuperIcon'
import { buildPremiumPromptProperties, buildSignupAnalyticsContext } from '@/utils/signupPremiumAnalytics'
import { useLocation } from 'react-router-dom'

interface TrySuperButtonProps extends NavbarButtonBaseProps {
  /** Compact upgrade action with the full upgrade label available to assistive technology. */
  iconOnly?: boolean
}

function TrySuperButton({ disabled = false, iconOnly = false }: TrySuperButtonProps) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const user = useSelector(selectUser)
  const location = useLocation()

  if (user?.userIsSuper || !user?.userIsPremium) return null

  const handleClick = () => {
    const source = 'Header_CTA_Super'
    dispatch(
      setOpenModalTryPremium({
        isOpen: true,
        source,
        opened: true,
      })
    )
    const eventProperties = buildPremiumPromptProperties(
      buildSignupAnalyticsContext({
        source,
        type: 'Super',
        pathname: location.pathname,
      }),
    )
    customAmplitude('Premium Prompt', eventProperties)
    // @ts-ignore
    window.dataLayer.push({ event: 'premium_prompt', ...eventProperties })
  }

  if (iconOnly) {
    return (
      <DefaultButton
        type="secondary-black"
        icon="iconOnly"
        iconComponent={<SuperIcon disabled={disabled || !SyncManager.shared.hasConnection} />}
        ariaLabel={t('Upgrade to Super')}
        tooltip={t('Upgrade to Super')}
        disabled={disabled || !SyncManager.shared.hasConnection}
        onClick={handleClick}
      />
    )
  }

  return (
    <ButtonCustom
      type={SizeButton.STANDARD}
      kind={KIND.secondary}
      style={{
        minWidth: 'fit-content',
        width: '186px',
        gap: '6px',
        background: lightTheme.colors.grayScale50,
        opacity: !SyncManager.shared.hasConnection ? lightTheme.opacity.disabled : lightTheme.opacity.active,
      }}
      onClick={handleClick}
      disabled={disabled || !SyncManager.shared.hasConnection}
    >
      <SuperIcon size={16} disabled={disabled || !SyncManager.shared.hasConnection} />
      <p
        style={{
          ...lightTheme.typography.Small14Semibold,
          color: disabled ? lightTheme.colors.grayScale200 : lightTheme.colors.blackGray,
        }}
      >
        {t('Upgrade to Super')}
      </p>
    </ButtonCustom>
  )
}

export default TrySuperButton
